import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useConfigStore } from "@/stores/config";
import { usePermissions } from "@/composables/usePermissions";
import { useTrainingModes } from "@/composables/useTrainingModes";
import { useTablesStore } from "@/stores/tables";
import type { AclViewKey } from "@/config/aclViews";
import { subViewEntries } from "@/config/aclViews";
import { bootDiag } from "@/core/helpers/bootDiag";

/**
 * Redirect factory for section roots (e.g. /clients, /work, /finance, /company):
 * routes to the first sub-view the current user may access. Falls back to the
 * section's default page before rights are loaded or when nothing matches
 * (the guard re-checks once rights arrive).
 */
const redirectToFirstAllowedSubView = (parent: AclViewKey, fallback: string) => () => {
  const { availableSubViews } = usePermissions();
  const allowed = availableSubViews(parent);
  for (const [key, sv] of subViewEntries(parent)) {
    if (allowed.includes(key)) return sv.route;
  }
  return fallback;
};

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/dashboards/private/start",
    component: () => import("@/layout/Layout.vue"),
    meta: {
      middleware: "auth",
    },
    children: [
      // ── Dashboards (new unified routing) ────────────────────────────────────
      {
        path: "/dashboards",
        meta: { middleware: "auth" },
        children: [
          {
            // Start dashboard: user's personal scope='personal' dashboard
            path: "private/start",
            name: "dashboardPrivateStart",
            component: () => import("@/views/dashboards/PersonalDashboard.vue"),
            meta: { pageTitle: "Start", asideEnabled: true, sidebarEnabled: false },
          },
          {
            // A specific private dashboard by ID
            path: "private/:id",
            name: "dashboardPrivate",
            component: () => import("@/views/dashboards/PersonalDashboard.vue"),
            meta: { pageTitle: "Dashboard", asideEnabled: true, sidebarEnabled: false },
          },
          {
            // A company dashboard by ID
            path: "company/:dashboardid",
            name: "dashboardCompany",
            component: () => import("@/views/dashboards/CompanyDashboard.vue"),
            meta: { pageTitle: "Unternehmens-Dashboard", asideEnabled: true, sidebarEnabled: false },
          },
        ],
      },
      {
        path: "/calendar",
        redirect: "/calendar/trainings",
        meta: {
          middleware: "auth",
          aclView: 'Kalender' as AclViewKey,
        },
        children: [
          {
            path: "trainings",
            name: "kalender",
            component: () => import("@/views/calendar/Calendar_Manager.vue"),
            meta: {
              pageTitle: "Kalender",
              companySelector: true,
              asideEnabled: true,
              asidePrimaryDisabled: false,
              asideSecondaryDisabled: true,
              sidebarEnabled: true,
              sidebar: "calendar"
            },
          },
          {
            path: "series",
            name: "kalenderSerien",
            component: () => import("@/views/calendar/SeriesOverview.vue"),
            meta: {
              pageTitle: "Serien",
              companySelector: true,
              asideEnabled: true,
              // Normales Seitenlayout statt Kalenderlayout: die Sekundär-Aside
              // traegt hier das Kalendermenue (Trainings/Serien) und damit den
              // Weg zurueck. Die Kalender-Sidebar waere auf dieser Seite eine
              // Drag-Quelle ohne Ziel — es gibt kein Raster, in das man einen
              // Kurs ziehen koennte.
              asideSecondaryDisabled: false,
              sidebarEnabled: false,
            },
          },
        ]
      },
      {
        path: "/work",
        redirect: redirectToFirstAllowedSubView('Leistungen', '/work/overview'),
        meta: {
          middleware: "auth",
          aclView: 'Leistungen' as AclViewKey,
        },
        children: [
          {
            path: "overview",
            name: "workoverview",
            component: () => import("@/views/work/WorkOverview.vue"),
            meta: {
              pageTitle: "Übersicht",
              aclSubView: 'Uebersicht',
              companySelector: true,
              asideEnabled: true,
              asideSecondaryDisabled: false,
              sidebarEnabled: false,
            },
          },
          {
            path: "trainings/exercises",
            name: "exercisesoverview",
            component: () => import("@/views/work/WorkoutsOverview.vue"),
            meta: {
              pageTitle: "Übungen",
              aclSubView: 'Trainings',
              tab: "exercises",
              companySelector: true,
              asideEnabled: true,
              asideSecondaryDisabled: false,
              sidebarEnabled: false,
            },
          },
          {
            path: "trainings/workouts",
            name: "workoutslist",
            component: () => import("@/views/work/WorkoutsOverview.vue"),
            meta: {
              pageTitle: "Workouts",
              aclSubView: 'Trainings',
              tab: "workouts",
              companySelector: true,
              asideEnabled: true,
              asideSecondaryDisabled: false,
              sidebarEnabled: false,
            },
          },
          {
            path: "trainings/plans",
            name: "trainingplans",
            component: () => import("@/views/work/WorkoutsOverview.vue"),
            meta: {
              pageTitle: "Trainingspläne",
              aclSubView: 'Trainings',
              tab: "plans",
              companySelector: true,
              asideEnabled: true,
              asideSecondaryDisabled: false,
              sidebarEnabled: false,
            },
          },
          {
            path: "exercise",
            name: "exerciseeditor",
            component: () => import("@/views/work/ExerciseEditor.vue"),
            meta: {
              pageTitle: "Übung bearbeiten",
              aclSubView: 'Trainings',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "trainings/workout",
            name: "workoutbuilder",
            component: () => import("@/views/work/WorkoutBuilder.vue"),
            meta: {
              pageTitle: "Workout bearbeiten",
              aclSubView: 'Trainings',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "services",
            name: "workServices",
            component: () => import("@/views/work/ServicesOverview.vue"),
            meta: {
              pageTitle: "Leistungen & Trainings",
              aclSubView: 'Angebot',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "products/overview",
            name: "productslist",
            component: () => import("@/views/work/ProductsList.vue"),
            meta: {
              pageTitle: "Produkte",
              aclSubView: 'Angebot',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "products/categories",
            name: "productcategories",
            component: () => import("@/views/work/ProductCategories.vue"),
            meta: {
              pageTitle: "Produktkategorien",
              aclSubView: 'Angebot',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "products/stock",
            name: "productstock",
            component: () => import("@/views/work/ProductStock.vue"),
            meta: {
              pageTitle: "Lagerbestand & Inventur",
              aclSubView: 'Angebot',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "tasks",
            name: "workTasks",
            component: () => import("@/views/work/TasksOverview.vue"),
            meta: {
              pageTitle: "Todos",
              aclSubView: 'Arbeitsplanung',
              companySelector: true,
              asideEnabled: true,
              asideSecondaryDisabled: false,
              sidebarEnabled: false,
            },
          },
          {
            path: "trainingsreports",
            name: "trainingsreports",
            component: () => import("@/views/work/Trainingsreports.vue"),
            meta: {
              pageTitle: "Trainingsreports",
              aclSubView: 'Arbeitsplanung',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
        ]
      },
      {
        path: "/company/widgets",
        redirect: "/company/widgets/courses",
        meta: {
          middleware: "auth",
          aclView: 'Widgets' as AclViewKey,
        },
        children: [
          {
            path: "courses",
            name: "widgetsCourses",
            component: () => import("@/views/widgets/WidgetsCourses.vue"),
            meta: {
              pageTitle: "Kurswidgets",
              asideEnabled: true,
              companySelector: true,
            },
          },
          {
            path: "devices",
            name: "widgetsDevices",
            component: () => import("@/views/widgets/WidgetsDevices.vue"),
            meta: {
              pageTitle: "Tablets & Displays",
              asideEnabled: true,
              companySelector: true,
            },
          },
        ],
      },
      {
        path: "/widgets",
        redirect: "/company/widgets/courses",
      },
      {
        path: "/widgets/courses",
        redirect: "/company/widgets/courses",
      },
      {
        path: "/widgets/devices",
        redirect: "/company/widgets/devices",
      },
      {
        path: "/company",

        redirect: redirectToFirstAllowedSubView('Unternehmen', '/company/overview'),
        meta: {
          middleware: "auth",
          aclView: 'Unternehmen' as AclViewKey,
        },
        children: [
          {
            path: "overview",
            name: "companyoverview",
            component: () => import("@/views/company/overview.vue"),
            meta: {
              pageTitle: "Unternehmens Übersicht",
              aclSubView: 'Verwaltung',
              asideEnabled: true
            },
          },
          {
            path: "finance",
            name: "companyFinance",
            component: () => import("@/views/company/finance.vue"),
            meta: {
              pageTitle: "Finanzen – Einstellungen",
              aclSubView: 'Finanzen',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "costs",
            name: "companyCosts",
            component: () => import("@/views/finance/CostStructure.vue"),
            meta: {
              pageTitle: "Kostenstruktur",
              aclSubView: 'Finanzen',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "team",
            name: "companyStaff",
            component: () => import("@/views/company/staff.vue"),
            meta: {
              pageTitle: "Mitarbeiter",
              aclSubView: 'Mitarbeiter',
              asideEnabled: true
            },
          },
          {
            path: "team/:id",
            name: "companyTeamDetail",
            component: () => import("@/views/company/team/detail.vue"),
            meta: {
              pageTitle: "Mitarbeiter Details",
              aclSubView: 'Mitarbeiter',
              asideEnabled: true
            },
          },
          {
            path: "studios",
            name: "companyStudios",
            component: () => import("@/views/company/studios.vue"),
            meta: {
              pageTitle: "Company Studios",
              aclSubView: 'Verwaltung',
              asideEnabled: true
            },
          },
          {
            path: "acl",
            name: "companyACL",
            component: () => import("@/views/company/acl.vue"),
            meta: {
              pageTitle: "Zugangslevel",
              aclSubView: 'Mitarbeiter',
              asideEnabled: true
            },
          },
          {
            path: "teamplanner",
            name: "companyTeamPlanner",
            component: () => import("@/views/resources/Resources.vue"),
            meta: {
              pageTitle: "Ressourcenplanung",
              aclSubView: 'Mitarbeiter',
              asideEnabled: true
            },
          },
          {
            path: "openingtimes",
            name: "companyOpeningTimes",
            component: () => import("@/views/company/openingtimes.vue"),
            meta: {
              pageTitle: "Öffnungszeiten",
              aclSubView: 'Verwaltung',
              asideEnabled: true
            },
          },
          {
            path: "locations/:id",
            name: "companyLocation",
            component: () => import("@/views/company/Company_Location.vue"),
            meta: {
              pageTitle: "Niederlassung",
              aclSubView: 'Verwaltung',
              asideEnabled: true
            },
          },
          {
            path: "equipment",
            name: "companyEquipment",
            component: () => import("@/views/company/equipment/EquipmentView.vue"),
            meta: {
              pageTitle: "Geräte",
              aclSubView: 'Verwaltung',
              asideEnabled: true
            },
          },
          {
            path: "medialibrary",
            name: "companyMedia",
            component: () => import("@/views/company/MediaLibrary.vue"),
            meta: {
              pageTitle: "Medienbibliothek",
              aclSubView: 'Medien',
              asideEnabled: true
            },
          },
          {
            path: "templates",
            name: "companyTemplateCatalog",
            component: () => import("@/views/company/TemplateCatalog.vue"),
            meta: {
              pageTitle: "Vorlagen-Katalog",
              aclSubView: 'Medien',
              asideEnabled: true
            },
          },
        ]
      },
      // Alte Abonnement-Pfade → neuer Account-Bereich (Bookmarks bleiben gültig)
      { path: "/subscriptions", redirect: "/account/subscriptions" },
      { path: "/subscriptions/checkout", redirect: to => ({ path: "/account/subscriptions/checkout", query: to.query }) },
      {
        path: "/finance",
        redirect: redirectToFirstAllowedSubView('Finanzen', '/finance/cashpoint'),
        meta: { middleware: "auth", aclView: 'Finanzen' as AclViewKey },
        children: [
          {
            path: "cashpoint",
            name: "cashpoint",
            component: () => import("@/views/sales/CashPoint.vue"),
            meta: {
              pageTitle: "Verkauf",
              aclSubView: 'Kasse',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            // Adresse vor der Umbenennung auf "salesinvoices". Bleibt als
            // Weiterleitung stehen, damit Lesezeichen und offene Tabs nicht
            // ins Leere laufen.
            path: "invoices",
            redirect: "/finance/salesinvoices",
          },
          {
            path: "salesinvoices",
            name: "financeInvoices",
            component: () => import("@/views/finance/Rechnungen.vue"),
            meta: {
              pageTitle: "Rechnungen",
              aclSubView: 'Rechnungen',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "batch-transfers",
            name: "financeBatchTransfers",
            component: () => import("@/views/finance/Sammelueberweisungen.vue"),
            meta: {
              pageTitle: "Sammelüberweisungen",
              aclSubView: 'Sammelueberweisungen',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          // Beide Seiten sind in den Unternehmens-Bereich ("Finanzen") gewandert.
          // Alte Pfade bleiben als Redirect gültig (Bookmarks, Deep-Links).
          { path: "terms", redirect: to => ({ path: "/company/finance", query: to.query }) },
          { path: "costs", redirect: to => ({ path: "/company/costs", query: to.query }) },
          {
            path: "reconciliation",
            name: "financeReconciliation",
            component: () => import("@/views/finance/Zahlungsabgleich.vue"),
            meta: {
              pageTitle: "Zahlungsabgleich",
              aclSubView: 'Zahlungsabgleich',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: "mahnwesen",
            redirect: "/finance/mahnwesen/overview",
            children: [
              {
                path: "overview",
                name: "financeMahnwesen",
                component: () => import("@/views/finance/MahnwesenOverview.vue"),
                meta: {
                  pageTitle: "Mahnwesen",
                  aclSubView: 'Mahnwesen',
                  companySelector: true,
                  asideEnabled: true,
                  sidebarEnabled: false,
                },
              },
            ],
          },
        ]
      },

      {
        path: "/clients",
        redirect: redirectToFirstAllowedSubView('Kunden', '/clients/overview'),
        meta: {
          middleware: "auth",
          aclView: 'Kunden' as AclViewKey,
        },
        children: [
          {
            path: "overview",
            name: "clientsoverview",
            component: () => import("@/views/clients/ClientsOverview.vue"),
            meta: {
              pageTitle: "Clients",
              breadcrumbs: ["Clients", "Overview"],
              aclSubView: 'Liste',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: true,
            },
          },
          {
            path: "crm",
            name: "clientsCRM",
            component: () => import("@/views/clients/ClientsCRM.vue"),
            meta: {
              pageTitle: "Anfragemanagement",
              breadcrumbs: ["Clients", "CRM"],
              aclSubView: 'CRM',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: true,
            },
          },
          {
            path: "insights",
            name: "clientsInsights",
            component: () => import("@/views/clients/ClientsInsights.vue"),
            meta: {
              pageTitle: "Kunden Insights",
              breadcrumbs: ["Clients", "Insights"],
              aclSubView: 'Insights',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: true,
            },
          },
          {
            // Vor ":id", damit der Pfad nicht als Kunden-Id gelesen wird.
            // `requiresFreeTraining` blendet die Route aus, wenn das Studio nur
            // Kurstraining anbietet — der Guard unten leitet dann aufs
            // Dashboard, sonst wäre der Direktlink die Hintertür ums Menü.
            path: "attendance",
            name: "clientsAttendance",
            component: () => import("@/views/clients/Attendance.vue"),
            meta: {
              pageTitle: "Einlass",
              breadcrumbs: ["Clients", "Einlass"],
              aclSubView: 'Einlass',
              requiresFreeTraining: true,
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: true,
            },
          },
          {
            path: ":id",
            name: "clientDetail",
            component: () => import("@/views/clients/ClientDetail.vue"),
            meta: {
              pageTitle: "Client Details",
              breadcrumbs: ["Clients", "Details"],
              aclSubView: 'Liste',
              companySelector: true,
              asideEnabled: true,
              sidebarEnabled: true,
            },
          },
          {
            path: "bulk-import",
            name: "clientBulkImport",
            component: () => import("@/views/clients/BulkImportWizard.vue"),
            meta: {
              pageTitle: "Mitglieder importieren",
              asideEnabled: false,
              sidebarEnabled: false,
            },
          },
          {
            path: "process/new",
            name: "clientProcessNew",
            component: () => import("@/views/clients/FormProcessWizard.vue"),
            meta: {
              pageTitle: "Neukundenprozess",
              asideEnabled: false,
              sidebarEnabled: false,
            },
          },
          {
            path: "process/:clientId",
            name: "clientProcess",
            component: () => import("@/views/clients/FormProcessWizard.vue"),
            meta: {
              pageTitle: "Registrierungsprozess",
              asideEnabled: false,
              sidebarEnabled: false,
            },
          },
        ]
      },
      // ── Legacy /personal redirects (backwards compat) ─────────────────────
      {
        path: "/personal",
        redirect: "/dashboards/private/start",
        meta: { middleware: "auth" },
        children: [
          { path: "dashboard",          redirect: "/dashboards/private/start" },
          { path: "company-dashboard",  redirect: "/dashboards/private/start" },
          { path: "custom/:id",         redirect: (to) => ({ path: `/dashboards/private/${to.params.id}` }) },
          { path: "location-dashboard", redirect: "/dashboards/private/start" },
          {
            path: "workcalendar",
            name: "personalWorkcalendar",
            component: () => import("@/views/dashboards/addons/Arbeitskalender.vue"),
            meta: { pageTitle: "Arbeitskalender", asideEnabled: true, sidebarEnabled: false },
          },
        ],
      },
      {
        path: '/communication',
        redirect: redirectToFirstAllowedSubView('Kommunikation', '/communication/inbox'),
        meta: { middleware: 'auth', aclView: 'Kommunikation' as AclViewKey },
        children: [
          {
            path: 'overview',
            name: 'communicationOverview',
            component: () => import('@/views/communication/CommunicationOverview.vue'),
            meta: {
              pageTitle: 'Kommunikation',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          { path: 'gmail', redirect: '/communication/inbox' },
          {
            path: 'inbox',
            name: 'communicationInbox',
            component: () => import('@/views/communication/UnifiedInboxView.vue'),
            meta: {
              pageTitle: 'Nachrichten',
              aclSubView: 'Posteingang',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'chat',
            name: 'communicationChat',
            component: () => import('@/views/communication/ChatView.vue'),
            meta: {
              pageTitle: 'Chat',
              aclSubView: 'Chat',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          // ── Kampagnen ────────────────────────────────────────────────────
          // Eigener Sub-View, weil eine Kampagne mehr darf als ein Newsletter:
          // sie erzeugt Empfaengerlisten aus dem Kundenbestand.
          {
            path: 'campaigns',
            name: 'campaignsOverview',
            component: () => import('@/views/communication/CampaignsOverview.vue'),
            meta: {
              pageTitle: 'Kampagnen',
              aclSubView: 'Kampagnen',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'campaigns/:id',
            name: 'campaignDetail',
            component: () => import('@/views/communication/CampaignDetail.vue'),
            meta: {
              pageTitle: 'Kampagne',
              aclSubView: 'Kampagnen',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'campaigns/:id/social/:cardId',
            name: 'campaignSocial',
            component: () => import('@/views/communication/SocialStudio.vue'),
            meta: {
              pageTitle: 'Social-Kachel',
              aclSubView: 'Kampagnen',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },

          // ── Newsletter & Automation ──────────────────────────────────────
          {
            path: 'newsletter',
            name: 'newsletterOverview',
            component: () => import('@/views/communication/NewsletterOverview.vue'),
            meta: {
              pageTitle: 'Newsletter & Automation',
              aclSubView: 'Newsletter',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'newsletter/builder/:id?',
            name: 'newsletterBuilder',
            component: () => import('@/views/communication/NewsletterBuilder.vue'),
            meta: {
              pageTitle: 'Newsletter Builder',
              aclSubView: 'Newsletter',
              asideEnabled: false,
              sidebarEnabled: false,
            },
          },
          {
            path: 'newsletter/templates',
            name: 'newsletterTemplates',
            component: () => import('@/views/communication/NewsletterTemplates.vue'),
            meta: {
              pageTitle: 'Newsletter Vorlagen',
              aclSubView: 'Newsletter',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'newsletter/flows',
            name: 'newsletterFlows',
            component: () => import('@/views/communication/NewsletterFlows.vue'),
            meta: {
              pageTitle: 'Automation Flows',
              aclSubView: 'Newsletter',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'newsletter/flows/:id',
            name: 'flowEditor',
            component: () => import('@/views/communication/FlowEditor.vue'),
            meta: {
              pageTitle: 'Flow Editor',
              aclSubView: 'Newsletter',
              asideEnabled: false,
              sidebarEnabled: false,
            },
          },
        ],
      },
      // ── Account (Profil, Abonnements & Einstellungen) ────────────────────────
      {
        path: '/account',
        redirect: '/account/profil',
        meta: { middleware: 'auth' },
        children: [
          {
            path: 'profil',
            name: 'accountProfile',
            component: () => import('@/views/account/UserProfileView.vue'),
            meta: {
              pageTitle: 'Mein Profil',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'subscriptions',
            name: 'accountSubscriptions',
            component: () => import('@/views/company/Subscriptions.vue'),
            meta: {
              pageTitle: 'Abonnements',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'subscriptions/checkout',
            name: 'subscriptionsCheckout',
            component: () => import('@/views/subscriptions/CheckoutWizard.vue'),
            meta: {
              pageTitle: 'Abonnement abschließen',
              asideEnabled: false,
              sidebarEnabled: false,
            },
          },
          {
            path: 'settings',
            name: 'accountSettings',
            component: () => import('@/views/account/AccountSettings.vue'),
            meta: {
              pageTitle: 'Einstellungen',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
        ],
      },
      // Alte Einstellungs-Pfade → neuer Account-Bereich (Bookmarks bleiben gültig)
      { path: '/settings', redirect: '/account/profil' },
      { path: '/settings/overview', redirect: '/account/profil' },
      { path: '/settings/profile', redirect: '/account/profil' },
      { path: '/settings/account', redirect: '/account/subscriptions' },
      // ── Helpdesk ─────────────────────────────────────────────────────────────
      {
        path: '/helpdesk',
        redirect: '/helpdesk/overview',
        meta: { middleware: 'auth' },
        children: [
          {
            path: 'overview',
            name: 'helpdeskOverview',
            component: () => import('@/views/helpdesk/HelpdeskOverview.vue'),
            meta: {
              pageTitle: 'Helpdesk',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'chat/:ticketId?',
            name: 'helpdeskChat',
            component: () => import('@/views/helpdesk/HelpdeskChat.vue'),
            meta: {
              pageTitle: 'Support Chat',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
          {
            path: 'faq',
            name: 'helpdeskFaq',
            component: () => import('@/views/helpdesk/HelpdeskFaq.vue'),
            meta: {
              pageTitle: 'Wissensdatenbank',
              asideEnabled: true,
              sidebarEnabled: false,
            },
          },
        ],
      },
    ]
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
  {
    path: "/error",
    name: "error",
    component: () => import("@/views/general/Error.vue"),
    meta: {
      pageTitle: "Error General",
    },
  },
  {
    path: "/",
    component: () => import("@/views/general/auth/AuthLayout.vue"),
    children: [
      {
        path: "/sign-in",
        name: "sign-in",
        component: () => import("@/views/general/auth/SignIn.vue"),
        meta: {
          pageTitle: "Sign In",
        },
      },
      {
        path: "/sign-up",
        name: "sign-up",
        component: () => import("@/views/general/auth/SignUp.vue"),
        meta: {
          pageTitle: "Sign Up",
        },
      },
      {
        path: "/password-reset",
        name: "password-reset",
        component: () => import("@/views/general/auth/ForgotPassword.vue"),
        meta: {
          pageTitle: "Password reset",
        },
      },
      {
        path: "/password-reset/confirm",
        name: "password-reset-confirm",
        component: () => import("@/views/general/auth/ResetPassword.vue"),
        meta: {
          pageTitle: "Neues Passwort",
        },
      },
    ],
  },
  {
    // Oeffentliche Rechtstexte: /legal/agb, /legal/avv, /legal/datenschutz,
    // /legal/widerruf. Inhalt kommt aus mgmt_legal_documents ueber
    // GET /api/legal/:type — hier steht kein eigener Text.
    //
    // BEWUSST ohne `middleware: "auth"`: Google verlangt fuer die
    // OAuth-Verifizierung eine oeffentlich erreichbare Datenschutzerklaerung auf
    // derselben Domain wie die App, und eine Loginmaske erfuellt das nicht.
    path: "/legal/:type",
    name: "legal-document",
    component: () => import("@/views/general/LegalDocument.vue"),
    meta: {
      pageTitle: "Rechtstexte",
    },
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    component: () => import("@/views/general/Error.vue"),
    meta: {
      pageTitle: "Error 404",
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const configStore = useConfigStore();

  // current page view title
  // VITE_APP_NAME steht nur in der gitignorierten .env — in CI-/Docker-Builds
  // ist sie leer. Leere Teile darum herausfiltern, statt "undefined" in den
  // Tab-Titel zu schreiben.
  const pageTitle = [to.meta.pageTitle, import.meta.env.VITE_APP_NAME]
    .filter(Boolean)
    .join(" - ");
  if (pageTitle) document.title = pageTitle;

  // reset layout config
  configStore.resetLayoutConfig();

  // Aktiver Kunde: Links auf die Kundenübersicht direkt ins Kundenprofil umleiten.
  // Ausnahme: Navigation aus dem Kundenprofil selbst, damit die Übersicht erreichbar bleibt.
  if (to.name === "clientsoverview" && from.name !== "clientDetail") {
    const tablesStore = useTablesStore();
    const activeClientId = tablesStore.config.ActiveClient;
    if (activeClientId) {
      return next({ name: "clientDetail", params: { id: activeClientId } });
    }
  }

  // before page access check if page requires authentication
  if (to.meta.middleware == "auth") {
    if (authStore.isAuthenticated) {
      // ── ACL view permission check ──────────────────────────────────────────
      const aclView = to.matched.find(r => r.meta.aclView)?.meta.aclView as AclViewKey | undefined;
      if (aclView) {
        const tablesStore = useTablesStore();
        if (tablesStore.config.rightsloaded) {
          const { hasViewPermission, hasSubViewPermission } = usePermissions();
          if (!hasViewPermission(aclView)) {
            console.warn(`[Router] ACL denied for view '${aclView}' → redirecting to dashboard`);
            return next({ name: 'dashboardPrivateStart' });
          }
          // Granular sub-view check (e.g. Kunden → Liste / CRM / Insights)
          const aclSubView = to.matched.find(r => r.meta.aclSubView)?.meta.aclSubView as string | undefined;
          if (aclSubView && !hasSubViewPermission(aclView, aclSubView)) {
            console.warn(`[Router] ACL denied for sub-view '${aclView}.${aclSubView}' → redirecting to dashboard`);
            return next({ name: 'dashboardPrivateStart' });
          }
        }
        // If rights not yet loaded, allow through (will be enforced once rights arrive)
      }

      // ── Trainingsart-Gate (Einlass) ─────────────────────────────────────────
      // Routen mit `requiresFreeTraining` gibt es nur für Studios, die freies
      // Training anbieten. Dieselbe Quelle wie das Menü (useTrainingModes) —
      // zwei Auswertungen derselben Einstellung würden auseinanderlaufen, und
      // dann steht im Menü etwas, das die Route abweist.
      //
      // Solange companies_infos noch nicht synchronisiert ist, greifen die
      // Defaults (beides an) und die Route bleibt offen. Ein zu früh geöffneter
      // Einlass ist harmlos; ein Redirect beim ersten Laden wäre es nicht.
      const needsFreeTraining = to.matched.some(r => r.meta.requiresFreeTraining);
      if (needsFreeTraining) {
        const { showAccessControl } = useTrainingModes();
        if (!showAccessControl.value) {
          console.warn('[Router] Einlass deaktiviert (kein freies Training) → redirecting to dashboard');
          return next({ name: 'dashboardPrivateStart' });
        }
      }

      next();
    } else {
      authStore.checkifuserisLoggedin()
        .then(function (result) {
          if (result && result.id && result.kl) {
            next()
          } else {
            // Ohne diese Zeile ist auf prod nicht unterscheidbar, ob die
            // Sitzung fehlte oder die Prüfung selbst gescheitert ist.
            bootDiag('signin.guard-rejected', result === undefined ? 'check returned undefined' : 'no valid session row')
            next({ name: "sign-in" });
          }
        })
        .catch(function (err) {
          bootDiag('signin.guard-threw', err)
          next({ name: "sign-in" });
        })
    }
  } else {
    next();
  }

  // Scroll page to top on every route change
  window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
});
// router.beforeResolve(async to => {
//   console.log('beforeResolve')
//   const authStore = useAuthStore();
//   console.log(authStore.isAuthenticated)
//   if (!authStore.isAuthenticated) {
//     try {
//       let isUserAuthenticated = await authStore.checkifuserisLoggedin()
//       if (!isUserAuthenticated) {
//         console.log('beforeResolve')
//         return false
//       } else {
//         return true
//       }
//     } catch (error) {
//       console.log(error)
//       return false
//     }
//   }
// })
router.afterEach(() => {
  // intentionally empty — logging removed for production performance
})

export default router;
